// Map Firebase Auth error codes to user-facing messages.
//
// Two design choices baked in:
// 1. We never reveal whether an email exists. Identity Platform's
//    `enableImprovedEmailPrivacy` is on, so user-not-found and
//    wrong-password collapse to the same backend response — we mirror that
//    in the UI text rather than leaking the difference through different
//    copy.
// 2. Anything we don't have a specific message for falls back to a generic
//    "something went wrong" rather than Firebase's raw error code, which is
//    typically inscrutable to non-technical users.

export function authErrorMessage(error: unknown): string {
  const code = errorCode(error);

  switch (code) {
    case 'auth/invalid-email':
      return 'That email address looks malformed.';
    case 'auth/email-already-in-use':
      return 'An account with this email may already exist. Try signing in instead.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/missing-password':
      return 'Password is required.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Email or password is incorrect.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    case 'auth/requires-recent-login':
      return 'For security, please sign in again before completing this action.';
    case 'auth/invalid-verification-code':
    case 'auth/invalid-otp':
      return 'That code is incorrect. Try again.';
    case 'auth/code-expired':
      return 'That code has expired. Request a new one.';
    case 'auth/multi-factor-auth-required':
      // Caller should be detecting this and routing to the challenge UI;
      // surfacing it as a message means a flow bug, not a user error.
      return 'A second factor is required to complete sign-in.';
    case 'auth/unverified-email':
      return 'Verify your email first before setting up two-factor auth.';
    case 'auth/totp-challenge-timeout':
      return 'TOTP challenge timed out. Try again.';
    case 'auth/missing-phone-number':
      return 'Phone number is required.';
    case 'auth/invalid-phone-number':
      return 'That phone number looks malformed. Use international format, e.g. +447700900123.';
    default:
      return 'Something went wrong. Try again.';
  }
}

function errorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'code' in error) {
    const c = (error as { code: unknown }).code;
    return typeof c === 'string' ? c : undefined;
  }
  return undefined;
}
